// SPDX-FileCopyrightText: (C) 2023 Jason Ish <jason@codemonkey.net>
// SPDX-License-Identifier: MIT

import { A, useLocation, useNavigate, useParams } from "@solidjs/router";
import { Top } from "./Top";
import {
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  For,
  JSX,
  Match,
  onCleanup,
  onMount,
  Setter,
  Show,
  Switch,
  untrack,
} from "solid-js";
import { API, archiveEvent, getEventById, postComment } from "./api";
import { archiveAggregateAlert } from "./api";
import {
  Button,
  ButtonGroup,
  Col,
  Container,
  Dropdown,
  Row,
  Spinner,
  Tab,
  Tabs,
  Toast,
} from "solid-bootstrap";
import { prettyPrintJson } from "pretty-print-json";
import {
  AggregateAlert,
  EcsGeo,
  EveDns,
  EveSsh,
  EveTls,
  Event,
  EventWrapper,
} from "./types";
import { parse_timestamp } from "./datetime";
import { parseRule, RuleOption, ruleReferences } from "./rule";
import { automaticPcapSource, pcapErrorMessage } from "./PcapDownload";
import { eventFiles, FileActions } from "./FileDownload";
import { FlowEvents } from "./FlowEvents";
import { formatAddressWithPort, formatEventDescription } from "./formatters";
import { tinykeys } from "tinykeys";
import { eventIsArchived, eventIsEscalated, eventSetArchived } from "./event";
import { eventStore } from "./eventstore";
import { addError, addNotification } from "./Notifications";
import { eventNameFromType } from "./Events";
import { EventServiceConfig, serverConfig } from "./config";
import { createStore } from "solid-js/store";
import { BiGear, BiInfoCircle, BiQuestionCircle } from "./icons";
import { SearchLink } from "./common/SearchLink";
import * as api from "./api";
import { FormattedTimestamp, AutoArchiveMenuElements } from "./components";
import * as bootstrap from "bootstrap";
import { base64ToBytes, prettyHex } from "./fileview/hex";

// A card-header action sized to sit on the header's text line without
// growing it.
const CARD_HEADER_BUTTON_STYLE =
  "--bs-btn-padding-y: .075rem; --bs-btn-padding-x: .5rem; --bs-btn-font-size: .8rem;";

const PCAP_BUTTON_STYLE =
  "--bs-btn-padding-y: .1rem; --bs-btn-padding-x: .2rem; --bs-btn-font-size: .7rem;";

interface HistoryEntry {
  action: string;
  timestamp: string;
  username?: string;
  cause?: string;
  comment?: string;
}

export function EventView() {
  console.log("***** EventView *****");
  const params = useParams<{ id: string }>();
  const [eventId, setEventId] = createSignal<null | string>(null);
  const [event, setEvent] = createSignal<EventWrapper>();
  const [eventDetails, setEventDetails] = createSignal<any[][]>();
  const [commonDetails, setCommonDetails] = createSignal<any[][]>();
  const [showCopyToast, setShowCopyToast] = createSignal(false);
  const [history, setHistory] = createSignal<HistoryEntry[]>([]);
  const [showCommentForm, setShowCommentForm] = createSignal(false);
  const [geoIp, setGeoIp] = createStore<{
    source: EcsGeo | undefined;
    destination: EcsGeo | undefined;
  }>({ source: undefined, destination: undefined });
  const navigate = useNavigate();
  const location = useLocation();
  let keybindings: any = null;
  let referer: null | string = null;
  let rawJsonRef: any = null;

  const [srcIpDns, setSrcIpDns] = createSignal<null | any>(null);
  const [destIpDns, setDestIpDns] = createSignal<null | any>(null);

  const [pcapPending, setPcapPending] = createSignal(false);
  const [pcapSources, setPcapSources] = createSignal<API.PcapSource[]>();
  const [pcapRouting, setPcapRouting] = createSignal<API.PcapRouting>();

  // Files the event references that may be downloadable from a file
  // store; empty when file retrieval is unavailable.
  const downloadableFiles = createMemo(() =>
    serverConfig()?.filestore != null ? eventFiles(event()?._source) : [],
  );

  // The preview and download actions for a file card's header.
  const fileDownloadAddOn = () => (
    <Show when={downloadableFiles().length > 0}>
      <FileActions
        eventId={String(event()!._id)}
        files={downloadableFiles()}
        event={event()?._source}
        style={CARD_HEADER_BUTTON_STYLE}
      />
    </Show>
  );

  const hasFlowAddresses = () =>
    !!(event()?._source.src_ip && event()?._source.dest_ip);
  const pcapAvailability = () =>
    automaticPcapSource(event()?._source, pcapSources(), pcapRouting());
  const quickPcapAvailable = () => pcapAvailability().ok;

  // Controller for the in-flight pcap download, letting the user (or
  // navigation away from the view) cancel the server-side extraction.
  let pcapAbort: AbortController | undefined;

  // Download the pcap for the current event, buffered so limits and
  // errors surface as toasts. An explicit source (from the dropdown
  // menu) bypasses the server's routing.
  const downloadPcap = async (source?: string) => {
    const ev = event();
    // Do not rely on the button's disabled attribute as the only guard for
    // automatic downloads. Explicit dropdown selections intentionally bypass
    // this check so an operator can choose a different capture source.
    if (!ev || (source === undefined && !quickPcapAvailable())) return;
    const controller = new AbortController();
    pcapAbort = controller;
    setPcapPending(true);
    try {
      // The quick button buffers the fixed-size capture so truncation
      // and no-match errors surface here. The custom page handles the
      // larger native streaming case.
      const result = await API.fetchPcap(
        String(ev._id),
        controller.signal,
        source,
      );
      if (result.truncated) {
        addNotification("Capture truncated by server size/time limits");
      }
    } catch (err: any) {
      if (err?.name === "AbortError") {
        // User cancelled (or navigated away); not an error.
      } else if (err instanceof API.PcapError) {
        addError(pcapErrorMessage(err));
      } else {
        addError(`PCAP request failed: ${err}`);
      }
    } finally {
      // A stale request cancelled during same-route navigation must not
      // clear the pending state of a newer event's request.
      if (pcapAbort === controller) {
        pcapAbort = undefined;
        setPcapPending(false);
      }
    }
  };

  // While a download is pending the PCAP button acts as a Cancel
  // control instead.
  const onPcapClick = () => {
    if (pcapPending()) {
      pcapAbort?.abort();
    } else {
      downloadPcap();
    }
  };

  // Cancel any in-flight download and reset the button to idle. The
  // aborted request rejects with an AbortError that downloadPcap's
  // catch swallows silently. Called on view disposal and when the
  // viewed event changes on the same route (which reuses this
  // component instance, so onCleanup does not fire).
  const cancelPcapDownload = () => {
    pcapAbort?.abort();
    pcapAbort = undefined;
    setPcapPending(false);
  };

  // Cancel any in-flight download when leaving the view so the
  // server-side extraction is stopped too.
  onCleanup(cancelPcapDownload);

  // Refresh the point-in-time source list whenever this component moves to a
  // different event. Until discovery succeeds, the quick action stays
  // disabled because it cannot confirm that the event's source is available.
  createEffect(() => {
    if (!event() || serverConfig()?.pcap == null) {
      setPcapSources(undefined);
      return;
    }

    setPcapSources(undefined);
    const controller = new AbortController();
    API.getPcapSources(controller.signal)
      .then(setPcapSources)
      .catch(() => {
        // Keep the quick action disabled when availability is unknown.
      });
    // A failed routing fetch leaves the implicit mirror in place; the
    // server is authoritative either way.
    API.getPcapRouting(controller.signal)
      .then(setPcapRouting)
      .catch(() => {});
    onCleanup(() => controller.abort());
  });

  console.log(`- EventView: EVENT_STORE.active_id=${eventStore.active?._id}`);

  onMount(() => {
    const ignoreKey = (e: any) => {
      return ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName);
    };

    keybindings = tinykeys(window, {
      u: (e: any) => {
        if (ignoreKey(e)) {
          return;
        }
        window.history.back();
      },
      e: (e: any) => {
        if (ignoreKey(e)) {
          return;
        }
        archiveAlert();
      },
      f8: () => {
        archiveAlert();
      },
      ".": (e: any) => {
        if (ignoreKey(e)) {
          return;
        }
        let element = document.getElementById("tool-menu");
        if (element) {
          element.click();
          element.focus();
        }
      },
    });

    referer = (location.state as any)?.referer || null;
    if (referer) {
      console.log("EventView.onMount: referer=" + referer);
    }
  });

  onCleanup(() => {
    if (keybindings) {
      keybindings();
    }
  });

  createEffect(() => {
    const _event = event();
    const _srcIpDns = srcIpDns();
    const _destIpDns = destIpDns();
    const popoverTriggerList = document.querySelectorAll(
      '[data-bs-toggle="popover"]',
    );
    const popoverList = [...popoverTriggerList].map(
      (popoverTriggerEl) => new bootstrap.Popover(popoverTriggerEl),
    );
  });

  createEffect(() => {
    setEventId(params.id);
  });

  // Update the history signal.
  createEffect(() => {
    const history = event()?._source?.evebox?.history;
    if (history) {
      setHistory(history);
    } else {
      setHistory([]);
    }
  });

  // Update GeoIP information.
  createEffect(() => {
    // Check for SELKS style first.
    if (event()?._source.geoip) {
      const geoip = event()?._source.geoip;
      if (geoip?.country_name) {
        let geo = {
          country_name: geoip?.country_name,
        };
        let source = undefined;
        let destination = undefined;
        if (geoip.ip === event()?._source.src_ip) {
          source = geo;
        } else {
          destination = geo;
        }
        setGeoIp({ source: source, destination: destination });
        return;
      }
    }

    const source =
      event()?._source.source?.geo || event()?._source.geoip_source;
    const destination =
      event()?._source.destination?.geo || event()?._source.geoip_destination;

    // Elastic ECS or EveBox log reader.
    setGeoIp({
      source: source && Object.keys(source).length ? source : undefined,
      destination:
        destination && Object.keys(destination).length
          ? destination
          : undefined,
    });
  });

  createEffect(() => {
    console.log(`EventView.createEffect: Loading event ID: ${eventId()}`);

    // Navigating between events reuses this component instance, so
    // onCleanup does not run. Cancel any download still in flight for
    // the previous event before loading the new one, so a stale
    // download's completion side effects (the truncation toast and file
    // download) and the Cancel control are not misattributed
    // to the event now shown. A no-op when nothing is in flight
    // (including the initial load), so it never aborts spuriously.
    cancelPcapDownload();

    untrack(() => {
      console.log(`-- Requested event ID: ${params.id}`);
      console.log(`-- Active event ID: ${eventStore.active?._id}`);
      console.log(`-- Events in store: ${eventStore.events.length}`);
      refreshEvent();
    });
  });

  // Find DNS.
  createEffect(async () => {
    let source = event()?._source;
    if (!source) {
      return;
    }
    const src_ip = source.src_ip;
    const dest_ip = source.dest_ip;
    const host = source.host;

    setSrcIpDns(null);
    setDestIpDns(null);
    if (!src_ip || !dest_ip) {
      return;
    }

    // Use flow start if available, otherwise use the event timestamp.
    const before = source.flow?.start || source.timestamp;

    api
      .get("/api/find-dns", {
        src_ip: src_ip,
        dest_ip: dest_ip,
        before,
        host,
      })
      .then((response) => {
        if (response.data.rrnames?.length > 0) {
          setSrcIpDns(response.data.rrnames);
        }
      });

    api
      .get("/api/find-dns", {
        src_ip: dest_ip,
        dest_ip: src_ip,
        before,
        host,
      })
      .then((response) => {
        if (response.data.rrnames?.length > 0) {
          setDestIpDns(response.data.rrnames);
        }
      });
  });

  const refreshEvent = () => {
    getEventById(params.id)
      .then((event) => {
        if (eventStore.active && eventStore.active._id == params.id) {
          // Copy (by reference) the metadata and tags from the partial
          // event in the store so the archive and escalation states are
          // reflected when the user clicks back to the alerts view.
          event._metadata = eventStore.active._metadata;
          event._source.tags = eventStore.active._source.tags;
        }
        setEvent(event);
      })
      .catch(() => {
        setEvent(undefined);
      });
  };

  createEffect(() => {
    let source = event()?._source;

    if (!source) {
      setEventDetails(undefined);
      setCommonDetails(undefined);
      return;
    }

    let commonDetails = [];

    if (source.timestamp) {
      commonDetails.push([
        "Timestamp",
        <SearchLink field={"timestamp"} value={source.timestamp}>
          <FormattedTimestamp timestamp={source.timestamp} withMillis={true} />
        </SearchLink>,
      ]);
    }

    if (source.host) {
      commonDetails.push(["Sensor", source.host]);
    }
    if (source.proto) {
      commonDetails.push(["Protocol", source.proto]);
    }
    if (source.src_ip) {
      commonDetails.push([
        "Source",
        <>
          <SearchLink field={"@ip"} value={source.src_ip}>
            {formatAddressWithPort(source.src_ip, source.src_port)}
          </SearchLink>
          <A class={"ps-1"} href={`/dashboards/address/${source.src_ip}`}>
            <BiInfoCircle class={"bi-inline"} />
          </A>
        </>,
      ]);
    }
    if (source.dest_ip) {
      commonDetails.push([
        "Destination",
        <>
          <SearchLink field={"@ip"} value={source.dest_ip}>
            {formatAddressWithPort(source.dest_ip, source.dest_port)}
          </SearchLink>
          <A class={"ps-1"} href={`/dashboards/address/${source.dest_ip}`}>
            <BiInfoCircle class={"bi-inline"} />
          </A>
        </>,
      ]);
    }
    if (source.in_iface) {
      commonDetails.push(["In Interface", source.in_iface]);
    }
    if (source.flow_id) {
      commonDetails.push([
        "Flow ID",
        <SearchLink field={"flow_id"} value={source.flow_id} />,
      ]);
    }
    if (source.community_id) {
      commonDetails.push([
        "Community ID",
        <SearchLink field={"community_id"} value={source.community_id} />,
      ]);
    }

    if (source.ether?.src_mac) {
      commonDetails.push([
        "Source MAC",
        <SearchLink value={source.ether.src_mac} />,
      ]);
    }

    if (source.ether?.dest_mac) {
      commonDetails.push([
        "Dest MAC",
        <SearchLink value={source.ether.dest_mac} />,
      ]);
    }

    setCommonDetails(commonDetails);

    if (event()?._source.event_type === "alert") {
      let alert = event()!._source.alert!;
      let eventDetails = [
        [
          "Signature",
          <SearchLink field={"alert.signature"} value={alert.signature} />,
        ],
        [
          "Category",
          <SearchLink field={"alert.category"} value={alert.category} />,
        ],
        ["Severity", alert.severity],
        [
          "Signature ID",
          <SearchLink
            field={"alert.signature_id"}
            value={alert.signature_id}
          />,
        ],
        ["Generator ID", alert.gid],
        ["Revision", alert.rev],
      ];
      const references = ruleReferences(
        alert.rule ? parseRule(alert.rule) : undefined,
        alert.references || [],
      ).filter((reference) => reference.url);
      if (references.length > 0) {
        eventDetails.push([
          "References",
          <For each={references}>
            {(reference) => (
              <div>
                <ExternalLink href={reference.url}>
                  {reference.value}
                </ExternalLink>
              </div>
            )}
          </For>,
        ]);
      }
      setEventDetails(eventDetails);
    } else if (event()?._source.event_type === "stats") {
      const stats = event()!._source.stats!;
      let eventDetails = [];
      if (stats.uptime) {
        eventDetails.push(["Uptime", stats.uptime]);
      }
      if (stats.capture?.kernel_packets) {
        eventDetails.push(["Kernel packets", stats.capture.kernel_packets]);
      }
      if (stats.capture?.kernel_drops) {
        eventDetails.push(["Kernel drops", stats.capture.kernel_drops]);
      }
      if (stats.decoder?.pkts) {
        eventDetails.push(["Decoder packets", stats.decoder.pkts]);
      }
      if (stats.decoder?.bytes) {
        eventDetails.push(["Decoder bytes", stats.decoder.bytes]);
      }
      if (stats.detect?.alert) {
        eventDetails.push(["Alerts", stats.detect.alert]);
      }
      setEventDetails(eventDetails);
    } else if (event()?._source.event_type === "quic") {
      const quic = event()!._source.quic!;
      const eventDetails: any[][] = [];
      if (quic.sni) {
        eventDetails.push([
          "SNI",
          <SearchLink field={"quic.sni"} value={quic.sni} />,
        ]);
      }
      if (quic.version) {
        eventDetails.push(["Version", quic.version]);
      }
      const alpn = quic.extensions?.find((e: any) => e.name === "alpn");
      if (alpn?.values?.length) {
        eventDetails.push(["ALPN", alpn.values.join(", ")]);
      }
      if (quic.ja4) {
        eventDetails.push([
          "JA4",
          <SearchLink field={"quic.ja4"} value={quic.ja4} />,
        ]);
      }
      if (quic.ja3?.hash) {
        eventDetails.push([
          "JA3",
          <SearchLink field={"quic.ja3.hash"} value={quic.ja3.hash} />,
        ]);
      }
      if (quic.ja3s?.hash) {
        eventDetails.push([
          "JA3S",
          <SearchLink field={"quic.ja3s.hash"} value={quic.ja3s.hash} />,
        ]);
      }
      if (quic.extensions?.length) {
        eventDetails.push(["Extensions", quic.extensions.length]);
      }
      setEventDetails(eventDetails.length ? eventDetails : undefined);
    } else if (event()?._source.event_type === "dns") {
      setEventDetails(nonEmpty(dnsDetails(event()!._source.dns!)));
    } else if (event()?._source.event_type === "tls") {
      setEventDetails(nonEmpty(tlsDetails(event()!._source.tls!)));
    } else if (event()?._source.event_type === "ssh") {
      setEventDetails(nonEmpty(sshDetails(event()!._source.ssh!)));
    } else if (event()?._source.event_type === "mqtt") {
      setEventDetails(nonEmpty(mqttDetails(event()!._source.mqtt!)));
    } else if (event()?._source.event_type === "mdns") {
      const mdns = event()!._source.mdns!;
      const eventDetails: any[][] = [];
      if (mdns.type) {
        eventDetails.push(["Type", mdns.type.toUpperCase()]);
      }
      if (mdns.flags?.length) {
        eventDetails.push(["Flags", mdns.flags.join(", ")]);
      }
      if (mdns.rcode) {
        eventDetails.push(["RCODE", mdns.rcode]);
      }
      for (const q of mdns.queries || []) {
        eventDetails.push([
          "Query",
          <>
            {q.rrtype?.toUpperCase()}{" "}
            <SearchLink value={q.rrname}>{q.rrname}</SearchLink>
          </>,
        ]);
      }
      for (const [label, records] of [
        ["Answer", mdns.answers],
        ["Additional", mdns.additionals],
      ] as [string, any[] | undefined][]) {
        for (const rr of records || []) {
          eventDetails.push([label, <MdnsRecord rr={rr} />]);
        }
      }
      setEventDetails(eventDetails.length ? eventDetails : undefined);
    } else {
      // Generic fallback: show the scalar fields of the object named
      // after the event type (e.g. `tls`, `http`, `flow`) so the
      // details panel isn't empty for types without a specific handler.
      const eventType = event()?._source.event_type;
      const obj = eventType ? (event()!._source as any)[eventType] : null;
      if (obj && typeof obj === "object" && !Array.isArray(obj)) {
        const eventDetails: any[][] = [];
        for (const [key, value] of Object.entries(obj)) {
          if (value === null || value === undefined) continue;
          if (typeof value === "object") continue;
          eventDetails.push([
            key,
            <SearchLink field={`${eventType}.${key}`} value={value} />,
          ]);
        }
        setEventDetails(eventDetails.length ? eventDetails : undefined);
      } else {
        setEventDetails(undefined);
      }
    }
  });

  function copyRawJson() {
    const e: HTMLInputElement | null = document.getElementById(
      "raw-json",
    ) as HTMLInputElement;
    if (e) {
      e.select();
      window.navigator.clipboard.writeText(e.value);
      addNotification("JSON copied to clipboard");
    }
  }

  // From: https://stackoverflow.com/a/2838358
  //
  // Copied from Stack Overflow.  Highlights the text inside an element.
  function selectElementText(el: HTMLElement) {
    if (window.getSelection && document.createRange) {
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        range.selectNodeContents(el);
        sel.removeAllRanges();
        sel.addRange(range);
        return;
      }
    }
    addNotification("Select not supported in this browser");
  }

  function selectRawJson() {
    const e: HTMLInputElement | null = document.getElementById(
      "formatted-json",
    ) as HTMLInputElement;
    if (e) {
      selectElementText(e);
    }
  }

  function isAggregateAlert(): boolean {
    return event()?._metadata?.count! > 0;
  }

  function isEscalated(): boolean {
    let ev = event();
    if (ev) {
      if (ev._metadata) {
        if (
          ev._metadata.escalated_count > 0 &&
          ev._metadata.escalated_count === ev._metadata.count
        ) {
          return true;
        }
      } else {
        return eventIsEscalated(ev);
      }
    }
    return false;
  }

  function archiveAlert() {
    if (event()?._metadata) {
      const alert = event() as AggregateAlert;
      archiveAggregateAlert(alert).then(() => {});
      eventSetArchived(alert);
    } else if (event()) {
      archiveEvent(event()!);
    }

    goBack();
  }

  async function escalate() {
    let ev = event();
    if (ev) {
      if (isAggregateAlert()) {
        await API.escalateAggregateAlert(ev);
        ev._metadata!.escalated_count = ev._metadata!.count;
      } else {
        await API.escalateEvent(ev);
      }
      refreshEvent();
    }
  }

  async function deEscalate() {
    let ev = event();
    if (ev) {
      if (isAggregateAlert()) {
        await API.deEscalateAggregateAlert(ev);
        ev._metadata!.escalated_count = 0;
      } else {
        await API.deEscalateEvent(ev);
      }
      refreshEvent();
    }
  }

  // Go "back". If it appears like we came here from an internal click on event, use the back button
  // so state is restored.  If not, go back "parent" view.
  function goBack() {
    if (location.state) {
      window.history.back();
    } else if (location.pathname.startsWith("/escalated")) {
      navigate("/escalated");
    } else if (location.pathname.startsWith("/inbox")) {
      navigate("/inbox");
    } else if (location.pathname.startsWith("/alerts")) {
      navigate("/alerts");
    } else if (location.pathname.startsWith("/event")) {
      navigate("/events");
    }
  }

  function OccurrenceLink(props: { children: any }) {
    if (event()?._source.alert) {
      const alert = event()!._source.alert!;
      let parts = [
        `alert.signature_id:${alert.signature_id}`,
        `src_ip:${event()?._source.src_ip}`,
        `dest_ip:${event()?._source.dest_ip}`,
        `@from:"${event()?._metadata?.min_timestamp}"`,
        `@to:"${event()?._metadata?.max_timestamp}"`,
      ];
      const url = "/events?q=" + parts.map(encodeURIComponent).join("+");
      return <A href={url}>{props.children}</A>;
    } else {
      return <></>;
    }
  }

  interface DisplayObject {
    title: string;
    key: string;
    rows: { key: string; val: any }[];
  }

  const [displayObjects, setDisplayObjects] = createSignal<DisplayObject[]>([]);

  const [objectColumns, setObjectColumns] = createStore<{
    col1: DisplayObject[];
    col2: DisplayObject[];
  }>({
    col1: [],
    col2: [],
  });

  createEffect(() => {
    let objects: DisplayObject[] = [];
    if (event()) {
      let source = event()!._source;
      for (const key of Object.keys(event()!._source)) {
        if (typeof source[key] === "object") {
          const flattened = flattenJson(source[key]);
          if (flattened.length > 0) {
            objects.push({
              key: key,
              title: eventNameFromType(key) || key.toUpperCase(),
              rows: flattened,
            });
          }
        }
      }
      objects.sort((a, b) => {
        if (a.rows.length < b.rows.length) {
          return 1;
        } else if (a.rows.length > b.rows.length) {
          return -1;
        } else {
          return 0;
        }
      });
      setDisplayObjects(objects);

      let card1_len = 0;
      let card2_len = 0;
      let card1: DisplayObject[] = [];
      let card2: DisplayObject[] = [];

      objects.forEach((object) => {
        if (card1_len === 0 || card1_len < card2_len) {
          card1.push(object);
          card1_len += object.rows.length;
          for (const row of object.rows) {
            if (row.val.length > 80) {
              card1_len += Math.ceil(row.val.length / 80);
            }
          }
        } else {
          card2.push(object);
          card2_len += object.rows.length;
          for (const row of object.rows) {
            if (row.val.length > 80) {
              card2_len += Math.ceil(row.val.length / 80);
            }
          }
        }
      });

      setObjectColumns({ col1: card1, col2: card2 });
    }
  });

  function getServiceLinks(event: EventWrapper): any[] {
    let serviceLinks = [];
    const eventServices = serverConfig()?.[
      "event-services"
    ] as EventServiceConfig[];
    if (eventServices) {
      for (let service of eventServices) {
        if (!service.enabled) {
          continue;
        }
        let url = service.url.replace(
          "{{raw}}",
          encodeURIComponent(JSON.stringify(event._source)),
        );
        if (serviceLinks.length > 0) {
          serviceLinks.push(" | ");
        }
        serviceLinks.push(<A href={url}>{service.name}</A>);
      }
    }

    return serviceLinks;
  }

  function eventToPcap(what: "packet" | "payload") {
    if (event()) {
      API.eventToPcap(event()!, what);
    }
  }

  const showToolMenu = createMemo(() => {
    console.log(event()?._source?.tags);
    const tags = event()?._source?.tags || [];
    const autoArchived =
      tags.indexOf("evebox.auto-archived") > -1 ||
      tags.indexOf("evebox.auto_archived") > -1;
    return event()?._source?.event_type == "alert" && !autoArchived;
  });

  const autoArchiveWithParams = (params: API.AddAutoArchiveRequest) => {
    const selectedEvent = event();
    const request = API.addAutoArchive(params);

    if ((params.dns_rrname || params.tls_sni) && selectedEvent?._metadata) {
      request
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Failed to add auto-archive filter: ${response.status}`,
            );
          }
          return archiveAggregateAlert(selectedEvent, params);
        })
        .then(() => {
          eventStore.events = [];
          eventStore.active = null;
          addNotification(
            "Auto-archive filter successfully added and matching events archived.",
          );
          goBack();
        })
        .catch((err) => addError(`${err}`));
      return;
    }

    archiveAlert();
  };

  return (
    <>
      <Top />
      <Container fluid={true} class={"mb-2"}>
        <Row>
          <Col class={"mt-2"}>
            <Button variant={"secondary"} class={"me-2"} onclick={goBack}>
              Back
            </Button>
            <Show when={event() && event()?._source.event_type === "alert"}>
              <Show when={eventIsArchived(event()!)}>
                <Button
                  variant={"outline-secondary"}
                  disabled={true}
                  class="me-2"
                >
                  Archive
                </Button>
              </Show>
              <Show when={!eventIsArchived(event()!)}>
                <Button
                  variant={"secondary"}
                  onclick={archiveAlert}
                  class={"me-2"}
                >
                  Archive{" "}
                  <Show when={isAggregateAlert()}>
                    ({event()?._metadata?.count})
                  </Show>
                </Button>
              </Show>
              <Show when={!isEscalated()}>
                <Button variant={"secondary"} class={"me-2"} onclick={escalate}>
                  Escalate
                </Button>
              </Show>
              <Show when={isEscalated()}>
                <Button
                  variant={"secondary"}
                  class={"me-2"}
                  onclick={deEscalate}
                >
                  De-escalate
                </Button>
              </Show>
            </Show>
            <Show when={serverConfig()?.pcap != null && hasFlowAddresses()}>
              {/* Split button: the primary buffers the fixed-size quick
                  capture; the custom action moves to the streaming page
                  with this event as editable starting context. */}
              <Dropdown as={ButtonGroup} class={"me-2"}>
                <Button
                  variant={"secondary"}
                  onclick={onPcapClick}
                  disabled={!pcapPending() && !quickPcapAvailable()}
                  title={
                    pcapPending()
                      ? "Cancel PCAP download"
                      : (pcapAvailability().reason ?? undefined)
                  }
                >
                  <Show when={pcapPending()} fallback={"PCAP"}>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />{" "}
                    Cancel fetch…
                  </Show>
                </Button>
                <Dropdown.Toggle
                  split
                  variant={"secondary"}
                  id={"pcap-options-dropdown"}
                  disabled={pcapPending()}
                >
                  <span class={"visually-hidden"}>PCAP options</span>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {/* Escape hatch: download from a specific source,
                      bypassing the server's routing. */}
                  <For each={pcapSources()}>
                    {(source) => (
                      <Dropdown.Item onClick={() => downloadPcap(source.name)}>
                        <Show
                          when={source.kind === "agent"}
                          fallback={"Download from EveBox server"}
                        >
                          Download from agent: {source.name}
                        </Show>
                      </Dropdown.Item>
                    )}
                  </For>
                  <Show when={pcapSources()?.length}>
                    <Dropdown.Divider />
                  </Show>
                  <A
                    class={"dropdown-item"}
                    href={
                      event()
                        ? `/pcap?event_id=${encodeURIComponent(
                            String(event()!._id),
                          )}`
                        : "/pcap"
                    }
                  >
                    Custom PCAP Download…
                  </A>
                </Dropdown.Menu>
              </Dropdown>
            </Show>
          </Col>
          <div class="col mt-2">
            <Show when={showToolMenu()}>
              <span class="dropdown float-end">
                <button
                  id="tool-menu"
                  class="btn btn-secondary dropdown-toggle pt-1 pb-2"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <BiGear />
                </button>
                <ul class="dropdown-menu">
                  <AutoArchiveMenuElements
                    event={event()}
                    callback={autoArchiveWithParams}
                  />
                </ul>
              </span>
            </Show>
          </div>
        </Row>

        <Show when={event()}>
          <div
            class={`mt-2 mb-2 alert ${bgClassForSeverity(event()!)}`}
            style={"padding: 0.5em;"}
          >
            <div class={"row"}>
              <div class={"col col-auto me-auto fw-bold"}>
                {formatTitle(event()!)}
              </div>
              <div class={"col col-auto"}>
                <Show when={getServiceLinks(event()!).length > 0}>
                  [ {getServiceLinks(event()!)} ]
                </Show>
                <Show when={isAggregateAlert()}>
                  &nbsp; [{" "}
                  <OccurrenceLink>
                    {event()?._metadata!.count} Occurrences
                  </OccurrenceLink>{" "}
                  ]
                </Show>
              </div>
            </div>
          </div>

          <Row>
            <Col class={"mb-2"} lg={12} xl={6}>
              <div class="card">
                <div class="card-header">Event</div>
                <div class="card-body app-card-body-compact">
                  <table class="table table-sm table-borderless table-striped table-hover app-detail-table mb-0">
                    <tbody>
                      <For each={commonDetails()}>
                        {(e) => (
                          <>
                            <tr>
                              <td>
                                <b>{e[0]}</b>
                              </td>
                              <td>{e[1]}</td>
                            </tr>
                          </>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </div>
            </Col>
            <Show when={eventDetails() || srcIpDns() || destIpDns()}>
              <Col class={"mb-2"} lg={12} xl={6}>
                <Show when={eventDetails()}>
                  <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                      {event()?._source.event_type?.toUpperCase()}
                      <Show when={event()?._source.event_type === "fileinfo"}>
                        {fileDownloadAddOn()}
                      </Show>
                    </div>
                    <div class="card-body app-card-body-compact">
                      <table
                        class={
                          "table table-sm app-detail-table table-borderless table-striped table-hover mb-0"
                        }
                      >
                        <tbody>
                          <For each={eventDetails()!}>
                            {(e) => (
                              <>
                                <tr>
                                  <td style={"min-width: 8em;"}>
                                    <b>{e[0]}</b>
                                  </td>
                                  <td>{e[1]}</td>
                                </tr>
                              </>
                            )}
                          </For>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Show>

                <Show when={srcIpDns() || destIpDns()}>
                  <div classList={{ card: true, "mt-2": !!eventDetails() }}>
                    <div class="card-header">
                      DNS Hostnames
                      <span
                        class="float-end"
                        data-bs-toggle="popover"
                        data-bs-content="DNS hostnames from Suricata DNS records"
                      >
                        <BiQuestionCircle />
                      </span>
                    </div>
                    <div class="card-body app-card-body-compact">
                      <table
                        class={
                          "table table-sm app-detail-table table-borderless table-striped table-hover mb-0"
                        }
                      >
                        <tbody>
                          <Show when={srcIpDns()}>
                            <For each={srcIpDns()}>
                              {(e) => (
                                <tr>
                                  <td>{event()?._source.src_ip}</td>
                                  <td>
                                    <SearchLink value={e}>{e}</SearchLink>
                                  </td>
                                </tr>
                              )}
                            </For>
                          </Show>
                          <Show when={destIpDns()}>
                            <For each={destIpDns()}>
                              {(e) => (
                                <tr>
                                  <td>{event()?._source.dest_ip}</td>
                                  <td>
                                    <SearchLink value={e}>{e}</SearchLink>
                                  </td>
                                </tr>
                              )}
                            </For>
                          </Show>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Show>
              </Col>
            </Show>
          </Row>

          {/* Rule */}
          <Show when={event()?._source?.alert?.rule}>
            <Row class={"mb-2"}>
              <Col>
                <div class="card">
                  <div class="card-header">Rule</div>
                  <div class="card-body">
                    <HighlightedRule rule={event()?._source.alert?.rule!} />
                  </div>
                </div>
              </Col>
            </Row>
          </Show>

          <FlowEvents event={event()!} />

          <History
            history={history()}
            eventId={eventId()!}
            setShowCommentForm={setShowCommentForm}
            showCommentForm={showCommentForm()}
            onChange={refreshEvent}
          />

          {/* Never show if there is history, as it embeds a comment form. */}
          <Show when={showCommentForm()}>
            <CommentEntry
              eventId={eventId()!}
              onChange={refreshEvent}
              close={() => setShowCommentForm(false)}
            />
          </Show>

          {/* GeoIP */}
          <Show when={geoIp.source || geoIp.destination}>
            <Row class={"mb-2"}>
              <Col>
                <div class="card">
                  <div class="card-header">GeoIP</div>
                  <div class="card-body p-0">
                    <table
                      class={
                        "mb-0 table table-sm table-striped table-bordered app-detail-table"
                      }
                    >
                      <tbody>
                        <Show when={geoIp.source}>
                          <tr>
                            <td>
                              <b>Source</b>
                            </td>
                            <td>{geoIpInfoString(geoIp.source)}</td>
                          </tr>
                        </Show>
                        <Show when={geoIp.destination}>
                          <tr>
                            <td>
                              <b>Destination</b>
                            </td>
                            <td>{geoIpInfoString(geoIp.destination)}</td>
                          </tr>
                        </Show>
                      </tbody>
                    </table>
                  </div>
                </div>
              </Col>
            </Row>
          </Show>

          <Show when={event() && event()!._source.stats}>
            <StatsCard stats={event()!._source.stats!} />
          </Show>

          {/* Tabbed? */}
          <Row>
            <Col class={"mb-2"}>
              <div class="card">
                <div class="card-body p-0">
                  <Tabs defaultActiveKey={"All"}>
                    <Tab eventKey="All" title="All">
                      {/* Object Cards */}
                      <Row>
                        <For each={[objectColumns.col1, objectColumns.col2]}>
                          {(col) => (
                            <>
                              <Col class={"col-sm-12 col-md-6"}>
                                <For each={col}>
                                  {(o) => (
                                    <>
                                      <div class="card m-2">
                                        <div class="card-header d-flex justify-content-between align-items-center">
                                          {o.title}
                                          {/* A fileinfo event's download
                                              sits on its details card. */}
                                          <Show
                                            when={
                                              o.key === "files" ||
                                              (o.key === "fileinfo" &&
                                                !eventDetails())
                                            }
                                          >
                                            {fileDownloadAddOn()}
                                          </Show>
                                        </div>
                                        <div class="card-body p-0">
                                          <table class="mb-0 table table-sm table-striped table-bordered app-detail-table">
                                            <tbody>
                                              <For each={o.rows}>
                                                {(e) => (
                                                  <>
                                                    <tr>
                                                      <td>{e.key}</td>
                                                      <td class="app-break-anywhere">
                                                        <Switch
                                                          fallback={
                                                            <SearchLink
                                                              value={e.val}
                                                            >
                                                              {e.val}
                                                            </SearchLink>
                                                          }
                                                        >
                                                          <Match
                                                            when={
                                                              e.val === true ||
                                                              e.val === false
                                                            }
                                                          >
                                                            {`${e.val}`}
                                                          </Match>
                                                          <Match
                                                            when={
                                                              o.key ==
                                                                "alert" &&
                                                              e.key == "rule"
                                                            }
                                                          >
                                                            <SearchLink
                                                              field={
                                                                "alert.signature"
                                                              }
                                                              value={
                                                                event()?._source
                                                                  .alert
                                                                  ?.signature
                                                              }
                                                            >
                                                              {e.val}
                                                            </SearchLink>
                                                          </Match>
                                                        </Switch>
                                                      </td>
                                                    </tr>
                                                  </>
                                                )}
                                              </For>
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </For>
                              </Col>
                            </>
                          )}
                        </For>
                      </Row>
                    </Tab>
                    <For each={displayObjects()}>
                      {(t, i) => {
                        return (
                          <>
                            <Tab eventKey={t.key} title={t.title}>
                              <table
                                class={
                                  "mb-0 table table-sm table-striped table-bordered app-detail-table"
                                }
                              >
                                <tbody>
                                  <For each={t.rows}>
                                    {(e) => (
                                      <>
                                        <tr>
                                          <th class={""} style={"width: 1%;"}>
                                            {e.key}
                                          </th>
                                          <td class="app-break-anywhere">
                                            <Switch
                                              fallback={
                                                <SearchLink value={e.val}>
                                                  {e.val}
                                                </SearchLink>
                                              }
                                            >
                                              <Match
                                                when={
                                                  t.key == "alert" &&
                                                  e.key == "rule"
                                                }
                                              >
                                                <SearchLink
                                                  field={"alert.signature"}
                                                  value={
                                                    event()?._source.alert
                                                      ?.signature
                                                  }
                                                >
                                                  {e.val}
                                                </SearchLink>
                                              </Match>
                                            </Switch>
                                          </td>
                                        </tr>
                                      </>
                                    )}
                                  </For>
                                </tbody>
                              </table>
                            </Tab>
                          </>
                        );
                      }}
                    </For>
                  </Tabs>
                </div>
              </div>
            </Col>
          </Row>

          {/* Payload */}
          <Show when={event()?._source.payload}>
            <Row class={"mb-2"}>
              <Col>
                <Base64BufferCard
                  title={"Payload"}
                  buffer={event()!._source.payload}
                  addOn={
                    <Button
                      onclick={() => eventToPcap("payload")}
                      style={PCAP_BUTTON_STYLE}
                    >
                      PCAP
                    </Button>
                  }
                />
              </Col>
            </Row>
          </Show>

          {/* Packet */}
          <Show when={event()?._source.packet}>
            <Row class={"mb-2"}>
              <Col>
                <Base64BufferCard
                  title={"Packet"}
                  buffer={event()!._source.packet}
                  addOn={
                    <Button
                      onclick={() => eventToPcap("packet")}
                      style={PCAP_BUTTON_STYLE}
                    >
                      PCAP
                    </Button>
                  }
                />
              </Col>
            </Row>
          </Show>

          <Row>
            <Col class={"mt-2"} sm={12} xxl={6}>
              <div class="card">
                <div class="card-header">Event Listing</div>
                <div class="card-body p-0">
                  <table
                    class={
                      "mb-0 table table-sm table-striped table-bordered app-detail-table"
                    }
                  >
                    <tbody>
                      <For each={flattenJson(event())}>
                        {(e) => (
                          <>
                            <tr>
                              <td>{e.key}</td>
                              <td class="app-break-anywhere">{e.val}</td>
                            </tr>
                          </>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </div>
            </Col>
            <Col class={"mt-2"} sm={12} xxl={6}>
              <div class="card">
                <div class="card-header">
                  JSON
                  <div class={"small float-end"}>
                    [
                    <Show
                      when={
                        window.location.protocol === "https:" ||
                        window.location.hostname === "localhost" ||
                        window.location.hostname === "127.0.0.1"
                      }
                    >
                      <a
                        href={""}
                        onclick={(e) => {
                          e.preventDefault();
                          copyRawJson();
                        }}
                      >
                        Copy
                      </a>
                      |
                    </Show>
                    <a
                      href={""}
                      onclick={(e) => {
                        e.preventDefault();
                        selectRawJson();
                      }}
                    >
                      Select
                    </a>
                    ]
                  </div>
                  <div style={"position: relative"}>
                    <div class={""} style={"position: fixed; right: 22px;"}>
                      <Toast
                        onClose={() => setShowCopyToast(false)}
                        show={showCopyToast()}
                        autohide
                        delay={10000}
                      >
                        <Toast.Body>JSON copied to clipboard</Toast.Body>
                      </Toast>
                    </div>
                  </div>
                </div>
                <div class="card-body">
                  <PrettyJson id={"formatted-json"} json={event()} />
                </div>
              </div>
            </Col>
          </Row>

          <textarea ref={rawJsonRef} id={"raw-json"} style={"display: none;"}>
            {JSON.stringify(event(), undefined, 4)}
          </textarea>
        </Show>
      </Container>
    </>
  );
}

function formatTitle(event: Event): string {
  try {
    return `${event._source.event_type.toUpperCase()}: ${formatEventDescription(
      event,
    )}`;
  } catch (err) {
    return JSON.stringify(event);
  }
}

function bgClassForSeverity(event: Event) {
  switch (event._source.alert?.severity) {
    case 1:
      return "alert-danger";
    case 2:
      return "alert-warning";
    default:
      return "alert-info";
  }
}

function formatTimestamp(timestamp: string) {
  const ts = parse_timestamp(timestamp);
  return ts.format("YYYY-MM-DD HH:mm:ss.SSS");
}

function flattenJson(
  object: any,
  prefix: string[] = [],
  output: { key: string; val: any }[] = [],
): { key: string; val: any }[] {
  if (object === null) {
    return output;
  }
  for (const key of Object.keys(object)) {
    let key_prefix = [...prefix];
    key_prefix.push(key);
    let val = object[key];
    switch (typeof val) {
      case "object":
        flattenJson(val, key_prefix, output);
        break;
      default:
        const full_key = key_prefix.join(".");
        if (!full_key.startsWith("__private.")) {
          output.push({ key: key_prefix.join("."), val: val });
        }
        break;
    }
  }

  return output;
}

function PrettyJson(props: any) {
  let output: any = undefined;

  // Copy and delete client side private fields.
  const json = { ...props.json };
  delete json.__private;

  createEffect(() => {
    if (output) {
      output.innerHTML = prettyPrintJson.toHtml(json, {
        quoteKeys: true,
        trailingCommas: false,
      });
    }
  });

  return (
    <>
      <pre
        ref={output}
        class="json-container app-break-anywhere app-pre-wrap app-overflow-hidden"
        id={props.id}
        style={props.style}
      ></pre>
    </>
  );
}

function Base64BufferCard(props: {
  title: string;
  buffer: string;
  addOn?: any;
}) {
  return (
    <div class="card">
      <div class="card-header">
        {props.title}
        <Show when={props.addOn}>
          <span class={"float-end"}>{props.addOn}</span>
        </Show>
      </div>
      <div class="card-body p-2">
        <Row>
          <Col md={12} xl={6} class={"pb-2"}>
            <div class="card">
              <div class="card-body p-2">
                <pre class="app-break-anywhere app-pre-wrap app-overflow-hidden">
                  {atob(props.buffer)}
                </pre>
              </div>
            </div>
          </Col>
          <Col md={12} xl={6}>
            <div class="card">
              <div class="card-body p-2">
                <table class={"m-0 table table-borderless table-striped"}>
                  <tbody>
                    <For each={prettyHex(base64ToBytes(props.buffer))}>
                      {(e) => (
                        <>
                          <tr class="app-hex-row">
                            <td class="app-hex-cell">
                              <pre class="app-hex-pre">{e[0]}</pre>
                            </td>
                            <td class="app-hex-cell">
                              <pre class="app-hex-pre">{e[1]}</pre>
                            </td>
                          </tr>
                        </>
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}

function HighlightedRule(props: { rule: string }) {
  const parsed = createMemo(() => parseRule(props.rule));

  return (
    <Show when={parsed()} fallback={<div class="app-rule">{props.rule}</div>}>
      <div class="app-rule">
        <span class="app-rule-header-action">{parsed()!.header[0]}</span>{" "}
        <span class="app-rule-header-proto">{parsed()!.header[1]}</span>{" "}
        <span class="app-rule-header-addr">{parsed()!.header[2]}</span>{" "}
        <span class="app-rule-header-port">{parsed()!.header[3]}</span>{" "}
        <span class="app-rule-header-direction">{parsed()!.header[4]}</span>{" "}
        <span class="app-rule-header-addr">{parsed()!.header[5]}</span>{" "}
        <span class="app-rule-header-port">{parsed()!.header[6]}</span> (
        <For each={parsed()!.options}>
          {(option) => <HighlightedRuleOption option={option} />}
        </For>
        )
      </div>
    </Show>
  );
}

function HighlightedRuleOption(props: { option: RuleOption }) {
  return (
    <>
      {props.option.leading}
      <span class="app-rule-keyword">
        {props.option.keyword}
        {props.option.value !== undefined ? ":" : ""}
      </span>
      <Show when={props.option.value !== undefined}>
        <span class="app-rule-keyword-value">{props.option.value}</span>
      </Show>
      {props.option.terminator}
    </>
  );
}

// Render a link to an external site, or just the text if there is no
// URL.
function ExternalLink(props: { href?: string; children: JSX.Element }) {
  return (
    <Show when={props.href} fallback={props.children}>
      <a href={props.href} target="_blank" rel="noopener noreferrer">
        {props.children}
      </a>
    </Show>
  );
}

function nonEmpty(rows: any[][]): any[][] | undefined {
  return rows.length ? rows : undefined;
}

function dnsDetails(dns: EveDns): any[][] {
  const rows: any[][] = [];
  if (dns.type) {
    rows.push(["Type", dns.type.toUpperCase()]);
  }
  // Suricata 8 (v3) puts queries in an array, older versions have
  // rrname/rrtype at the top level.
  const queries = dns.queries?.length
    ? dns.queries
    : dns.rrname
      ? [{ rrname: dns.rrname, rrtype: dns.rrtype }]
      : [];
  for (const q of queries) {
    rows.push([
      "Query",
      <>
        {q.rrtype}{" "}
        <SearchLink field={"dns.queries.rrname"} value={q.rrname}>
          {q.rrname}
        </SearchLink>
      </>,
    ]);
  }
  if (dns.rcode) {
    rows.push(["RCODE", dns.rcode]);
  }
  for (const a of dns.answers || []) {
    rows.push([
      "Answer",
      <>
        {a.rrtype} <SearchLink value={a.rrname}>{a.rrname}</SearchLink>
        <Show when={a.rdata !== undefined}>
          {" \u2192 "}
          <SearchLink value={a.rdata}>{a.rdata}</SearchLink>
        </Show>
        <Show when={a.soa}>
          {" \u2192 "}
          {a.soa?.mname} ({a.soa?.rname})
        </Show>
        <Show when={a.ttl !== undefined}>
          <span class="text-muted"> (TTL {a.ttl})</span>
        </Show>
      </>,
    ]);
  }
  for (const a of dns.authorities || []) {
    rows.push([
      "Authority",
      <>
        {a.rrtype} <SearchLink value={a.rrname}>{a.rrname}</SearchLink>
        <Show when={a.soa}>
          {" \u2192 "}
          {a.soa?.mname} ({a.soa?.rname})
        </Show>
      </>,
    ]);
  }
  return rows;
}

function tlsDetails(tls: EveTls): any[][] {
  const rows: any[][] = [];
  const t = tls as any;
  if (tls.sni) {
    rows.push(["SNI", <SearchLink field={"tls.sni"} value={tls.sni} />]);
  }
  if (tls.version) {
    rows.push([
      "Version",
      <SearchLink field={"tls.version"} value={tls.version} />,
    ]);
  }
  if (t.client_alpns?.length) {
    rows.push(["Client ALPN", t.client_alpns.join(", ")]);
  }
  if (t.server_alpns?.length) {
    rows.push(["Server ALPN", t.server_alpns.join(", ")]);
  }
  if (tls.subject) {
    rows.push([
      "Subject",
      <SearchLink field={"tls.subject"} value={tls.subject} />,
    ]);
  }
  if (tls.issuerdn) {
    rows.push([
      "Issuer",
      <SearchLink field={"tls.issuerdn"} value={tls.issuerdn} />,
    ]);
  }
  if (t.subjectaltname?.length) {
    rows.push(["Subject Alt Names", t.subjectaltname.join(", ")]);
  }
  if (tls.serial) {
    rows.push(["Serial", tls.serial]);
  }
  if (tls.fingerprint) {
    rows.push([
      "Fingerprint",
      <SearchLink field={"tls.fingerprint"} value={tls.fingerprint} />,
    ]);
  }
  if (tls.notbefore || tls.notafter) {
    rows.push([
      "Validity",
      `${tls.notbefore ?? "?"} \u2192 ${tls.notafter ?? "?"}`,
    ]);
  }
  if (tls.session_resumed !== undefined) {
    rows.push(["Session Resumed", tls.session_resumed ? "Yes" : "No"]);
  }
  if (t.ja4) {
    rows.push(["JA4", <SearchLink field={"tls.ja4"} value={t.ja4} />]);
  }
  if (tls.ja3?.hash) {
    rows.push([
      "JA3",
      <SearchLink field={"tls.ja3.hash"} value={tls.ja3.hash} />,
    ]);
  }
  if (tls.ja3s?.hash) {
    rows.push([
      "JA3S",
      <SearchLink field={"tls.ja3s.hash"} value={tls.ja3s.hash} />,
    ]);
  }
  return rows;
}

function sshDetails(ssh: EveSsh): any[][] {
  const rows: any[][] = [];
  for (const side of ["client", "server"] as const) {
    const s = (ssh as any)[side];
    if (!s) continue;
    const label = side === "client" ? "Client" : "Server";
    if (s.software_version) {
      rows.push([
        label,
        <>
          <SearchLink
            field={`ssh.${side}.software_version`}
            value={s.software_version}
          />
          <Show when={s.proto_version}>
            <span class="text-muted"> (protocol {s.proto_version})</span>
          </Show>
        </>,
      ]);
    }
    if (s.hassh?.hash) {
      rows.push([
        `${label} HASSH`,
        <SearchLink field={`ssh.${side}.hassh.hash`} value={s.hassh.hash} />,
      ]);
    }
  }
  return rows;
}

// MQTT events carry one object per message type in the transaction,
// keyed by the message type name (connect, publish, subscribe, ...).
function mqttDetails(mqtt: { [key: string]: any }): any[][] {
  const rows: any[][] = [];
  const order = [
    "connect",
    "connack",
    "publish",
    "puback",
    "pubrec",
    "pubrel",
    "pubcomp",
    "subscribe",
    "suback",
    "unsubscribe",
    "unsuback",
    "pingreq",
    "pingresp",
    "disconnect",
    "auth",
  ];
  const rank = (k: string) => {
    const i = order.indexOf(k);
    return i < 0 ? order.length : i;
  };
  const types = Object.keys(mqtt)
    .filter((k) => mqtt[k] && typeof mqtt[k] === "object")
    .sort((a, b) => rank(a) - rank(b));
  if (types.length) {
    rows.push(["Message", types.map((t) => t.toUpperCase()).join(", ")]);
  }
  for (const type of types) {
    const m = mqtt[type];
    switch (type) {
      case "connect":
        if (m.client_id) {
          rows.push([
            "Client ID",
            <SearchLink field={"mqtt.connect.client_id"} value={m.client_id} />,
          ]);
        }
        if (m.protocol_string || m.protocol_version) {
          rows.push([
            "Protocol",
            `${m.protocol_string ?? "MQTT"} ${m.protocol_version ?? ""}`.trim(),
          ]);
        }
        if (m.username) {
          rows.push(["Username", m.username]);
        }
        if (m.flags) {
          const flags = Object.entries(m.flags)
            .filter(([, v]) => v === true)
            .map(([k]) => k);
          if (flags.length) {
            rows.push(["Flags", flags.join(", ")]);
          }
        }
        if (m.will?.topic) {
          rows.push(["Will Topic", m.will.topic]);
        }
        break;
      case "connack":
        if (m.return_code !== undefined) {
          rows.push(["Return Code", m.return_code]);
        }
        if (m.session_present !== undefined) {
          rows.push(["Session Present", m.session_present ? "Yes" : "No"]);
        }
        break;
      case "publish":
        if (m.topic) {
          rows.push([
            "Topic",
            <SearchLink field={"mqtt.publish.topic"} value={m.topic} />,
          ]);
        }
        if (m.message !== undefined) {
          rows.push(["Payload", String(m.message)]);
        }
        break;
      case "subscribe":
      case "unsubscribe":
        for (const t of m.topics || []) {
          const topic = typeof t === "string" ? t : t.topic;
          const qos = typeof t === "object" ? t.qos : undefined;
          rows.push([
            type === "subscribe" ? "Subscribe" : "Unsubscribe",
            <>
              <SearchLink value={topic}>{topic}</SearchLink>
              <Show when={qos !== undefined}>
                <span class="text-muted"> (QoS {qos})</span>
              </Show>
            </>,
          ]);
        }
        break;
      case "suback":
        if (m.qos_granted) {
          rows.push(["QoS Granted", m.qos_granted.join(", ")]);
        }
        break;
    }
    if (type === "publish" && m.qos !== undefined) {
      const attrs = [`QoS ${m.qos}`];
      if (m.retain) attrs.push("retain");
      if (m.dup) attrs.push("dup");
      rows.push(["Delivery", attrs.join(", ")]);
    }
  }
  return rows;
}

// Render a single mDNS resource record. Suricata encodes the record
// data under a key named after the record type (`ptr`, `srv`, `a`,
// `txt`, ...) rather than a generic `rdata` field.
function MdnsRecord(props: { rr: { [key: string]: any } }) {
  const rr = props.rr;
  const skip = new Set(["rrname", "rrtype", "ttl", "flags"]);
  const key = Object.keys(rr).find((k) => !skip.has(k));
  const rrtype = key?.toUpperCase();
  const data = key ? rr[key] : undefined;

  const rdata = () => {
    if (data === undefined || data === null) {
      return null;
    }
    if (key === "srv" && typeof data === "object") {
      return (
        <>
          <SearchLink value={data.name}>{data.name}</SearchLink>:{data.port}{" "}
          (priority {data.priority ?? 0}, weight {data.weight ?? 0})
        </>
      );
    }
    if (Array.isArray(data)) {
      return <>{data.join(", ")}</>;
    }
    if (typeof data === "object") {
      return <>{JSON.stringify(data)}</>;
    }
    if (key === "nsec") {
      return <>{data}</>;
    }
    return <SearchLink value={data}>{data}</SearchLink>;
  };

  return (
    <>
      {rrtype} <SearchLink value={rr.rrname}>{rr.rrname}</SearchLink>
      <Show when={rdata()}>
        {" \u2192 "}
        {rdata()}
      </Show>
    </>
  );
}

function geoIpInfoString(geo: any) {
  let parts = [];
  if (geo.continent_name) {
    parts.push(geo.continent_name);
  }
  if (geo.country_name) {
    parts.push(geo.country_name);
  }
  if (geo.region_name) {
    parts.push(geo.region_name);
  }
  if (geo.city_name) {
    parts.push(geo.city_name);
  }
  return parts.join(" / ");
}

function StatsCard(props: { stats: { [key: string]: any } }) {
  const [hideZeros, setHideZeros] = createSignal(false);
  const [stats, setStats] = createSignal<{ key: string; val: any }[]>([]);
  const [filter, setFilter] = createSignal("");

  function toggleHide(e: any) {
    setHideZeros(e.target.checked);
  }

  createEffect(() => {
    const flattened = flattenJson(props.stats);
    const filtered = flattened.filter((e) => {
      if (hideZeros()) {
        if (e.val === 0) {
          return false;
        }
      }
      if (filter()) {
        if (e.key.indexOf(filter()) < 0) {
          return false;
        }
      }
      return true;
    });
    setStats(filtered);
  });

  return (
    <>
      <div class="card">
        <div class="card-header">
          Stats
          <div class="form-check float-end">
            <input
              class="form-check-input"
              type="checkbox"
              onChange={toggleHide}
            />
            <label class="form-check-label">Hide zeros</label>
          </div>
        </div>
        <div class="card-body p-0">
          <input
            type="text"
            class="form-control"
            placeholder="Filter..."
            oninput={(e) => {
              setFilter(e.target.value);
            }}
          />
          <table
            class="table table-striped table-hover table-bordered"
            style="width: 100%"
          >
            <tbody>
              <For each={stats()}>
                {(stat) => (
                  <>
                    <tr>
                      <td style="white-space: nowrap; width: 5%;">
                        {stat.key}
                      </td>
                      <td>{stat.val}</td>
                    </tr>
                  </>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function History(props: {
  eventId: string | number;
  history: HistoryEntry[];
  onChange: () => void;
  setShowCommentForm: Setter<boolean>;
  showCommentForm: boolean;
}) {
  const inputId = createUniqueId();

  const submitEvent = () => {
    let comment = (document.getElementById(inputId) as HTMLInputElement).value;
    postComment(props.eventId, comment).then(() => {
      props.onChange();
    });

    // Clear the comment.
    (document.getElementById(inputId) as HTMLInputElement).value = "";
  };

  return (
    <Show when={true}>
      <div class="row mb-2">
        <div class="col">
          <div class="card">
            <div class="card-header">History</div>
            <div class="card-body p-0">
              <ul class="list-group">
                <For each={props.history}>
                  {(entry) => (
                    <>
                      <li class="list-group-item">
                        {formatTimestamp(entry.timestamp).slice(0, -4)}
                        {" - "}
                        <Switch fallback={entry.action}>
                          <Match when={entry.action == "archived"}>
                            Archived
                          </Match>
                          <Match when={entry.action == "auto-archived"}>
                            Auto-archived
                          </Match>
                          <Match when={entry.action == "escalated"}>
                            Escalated
                          </Match>
                          <Match when={entry.action == "de-escalated"}>
                            De-escalated
                          </Match>
                          <Match when={entry.action == "comment"}>
                            Comment
                          </Match>
                        </Switch>
                        <Show when={entry.username}>
                          {" by "}
                          <i>{entry.username}</i>
                        </Show>
                        <Show when={!entry.username && entry.cause}>
                          {` (${historyCauseLabel(entry.cause)})`}
                        </Show>
                        <Show when={entry.action == "comment"}>
                          <p class="m-0">{entry.comment}</p>
                        </Show>
                      </li>
                    </>
                  )}
                </For>
              </ul>
            </div>
            <Show when={!props.showCommentForm}>
              <div class="card-footer">
                <div class="text-end">
                  <button
                    class="btn btn-primary"
                    onClick={() => props.setShowCommentForm(true)}
                  >
                    Add Comment
                  </button>
                </div>
              </div>
            </Show>
          </div>
        </div>
      </div>
    </Show>
  );
}

function historyCauseLabel(cause?: string): string | undefined {
  switch (cause) {
    case "filter":
      return "server filter";
    case "filter-query":
      return "server filter applied by query";
    case "age":
      return "age policy";
    case "metadata":
      return "alert metadata";
    default:
      return cause;
  }
}

function CommentEntry(props: {
  eventId: string | number;
  onChange: () => void;
  close: () => void;
}) {
  const inputId = createUniqueId();

  const submitEvent = () => {
    let comment = (document.getElementById(inputId) as HTMLInputElement).value;
    postComment(props.eventId, comment).then(() => {
      props.onChange();
    });

    // Clear the comment.
    (document.getElementById(inputId) as HTMLInputElement).value = "";

    // Close the comment form.
    props.close();
  };

  return (
    <>
      <div class="row mb-2">
        <div class="col">
          <form onSubmit={submitEvent}>
            <div class="card">
              <div class="card-header">Comment</div>
              <div class="card-body p-0">
                <textarea
                  id={inputId}
                  class="form-control"
                  placeholder="Enter a comment..."
                />
              </div>
              <div class="card-footer text-end">
                <button
                  type="submit"
                  class="btn btn-secondary me-2"
                  onClick={props.close}
                >
                  Close
                </button>
                <button type="submit" class="btn btn-primary">
                  Submit
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
